import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { 
  LayoutDashboard, Users, FolderKanban, Kanban, Truck, CheckSquare, 
  Zap, Settings, LogOut, Menu, X, FileText, DollarSign, FileStack,
  Trash2, Mail, Workflow, Receipt, MessageSquare, Bell, Sun,
  BarChart3, ClipboardCheck, Calendar, HeartPulse, ChevronRight, Plus
} from "lucide-react";

// Nav items with required permissions
const navItems = [
  { to: "/today", icon: Sun, label: "Today", permission: "*" },
  { to: "/", icon: LayoutDashboard, label: "Dashboard", permission: "dashboard:read" },
  { to: "/pipeline", icon: Kanban, label: "Pipeline", permission: "pipeline:read" },
  { to: "/inbox", icon: MessageSquare, label: "Inbox", permission: "*", badge: "sms_unread" },
  { to: "/contacts", icon: Users, label: "Contacts", permission: "contacts:read" },
  { to: "/projects", icon: FolderKanban, label: "Projects", permission: "projects:read" },
  { to: "/pipeline-health", icon: HeartPulse, label: "Health", permission: "pipeline:read" },
  { to: "/quotes", icon: FileText, label: "Quotes", permission: "quotes:read" },
  { to: "/billing", icon: Receipt, label: "Billing", permission: "billing:read" },
  { to: "/quote-templates", icon: FileStack, label: "Templates", permission: "templates:read" },
  { to: "/price-list", icon: DollarSign, label: "Price List", permission: "pricelist:read" },
  { to: "/inspections", icon: ClipboardCheck, label: "Inspections", permission: "inspections:read" },
  { to: "/schedule", icon: Calendar, label: "Schedule", permission: "schedule:read" },
  { to: "/analytics", icon: BarChart3, label: "Analytics", permission: "analytics:read" },
  { to: "/suppliers", icon: Truck, label: "Suppliers", permission: "suppliers:read" },
  { to: "/tasks", icon: CheckSquare, label: "Tasks", permission: "tasks:read" },
  { to: "/automations", icon: Zap, label: "Automations", permission: "automations:read" },
  { to: "/messaging-queue", icon: Bell, label: "Msg Queue", permission: "*", adminOnly: true },
  { to: "/stage-automations", icon: Workflow, label: "Stage Auto", permission: "*", adminOnly: true },
  { to: "/email-log", icon: Mail, label: "Email Log", permission: "*", adminOnly: true },
  { to: "/settings", icon: Settings, label: "Settings", permission: "*", adminOnly: true },
  { to: "/notifications", icon: Bell, label: "Notifications", permission: "*" },
  { to: "/trash", icon: Trash2, label: "Trash", permission: "*", adminOnly: true },
];

const ROLE_LABELS = {
  admin: "Admin",
  operations_manager: "Ops Manager",
  sales_manager: "Sales Manager",
  sales_rep: "Sales Rep",
  office: "Office",
};

export function Header({ title, subtitle, children }) {
  return (
    <div className="bg-white border-b border-slate-200 px-3 sm:px-6 py-3 sm:py-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-slate-800" data-testid="page-header">{title}</h1>
          {subtitle && <div className="mt-0.5">{subtitle}</div>}
        </div>
        {children && (
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 w-full sm:w-auto">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const location = useLocation();
  const { user, logout, hasPermission, isAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const filteredNavItems = navItems.filter(item => {
    if (item.adminOnly) return isAdmin;
    return hasPermission(item.permission);
  });

  // Poll SMS unread count so the Inbox sidebar item shows a live red badge.
  // Available to every authenticated user (not admin-only).
  const [smsUnread, setSmsUnread] = useState(0);
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL + "/api";
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        const r = await axios.get(`${API}/sms/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
        setSmsUnread(r.data?.count || 0);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [location.pathname]);

  // Mirror the polled unread count onto the native iOS app-icon badge so the
  // home-screen number always matches reality — 0 unread → no badge, N → N.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { setBadgeCount } = await import("../lib/badge");
        if (!cancelled) await setBadgeCount(smsUnread);
      } catch { /* not native — silent */ }
    })();
    return () => { cancelled = true; };
  }, [smsUnread]);

  // Native push registration — fires exactly once per session inside the iOS app.
  // No-op in a regular browser (the existing VAPID web-push flow handles those).
  const navigate = useNavigate();
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { initNativePush } = await import("../lib/nativePush");
        if (!cancelled) await initNativePush(navigate);
      } catch { /* silent — module absent in web build */ }
      try {
        // Keep the native iOS app-icon badge synced to the real unread-SMS
        // count on every app foreground event. Zero unread → no badge.
        const { initBadgeSync } = await import("../lib/badge");
        if (!cancelled) await initBadgeSync();
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user, navigate]);

  const NavContent = () => (
    <>
      {/* Logo */}
      <div className="p-4 border-b border-[#1a5a91]">
        <Link to="/" className="flex items-center gap-3" onClick={() => setSidebarOpen(false)}>
          <img src="https://customer-assets.emergentagent.com/job_e8b39f01-fa94-42d1-b033-5f5e8cd2cdfb/artifacts/i4ybnie3_C5B4C693-2106-4537-8F7F-AB72892ADD8D.png" alt="Tides Team" className="w-9 h-9 object-contain rounded flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-white font-bold text-lg leading-tight truncate">Tides Hub</h1>
            <p className="text-blue-300 text-xs truncate">{ROLE_LABELS[user?.role] || "User"}</p>
          </div>
        </Link>
      </div>

      {/* Nav Items */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        <div className="space-y-0.5">
          {filteredNavItems.map(({ to, icon: Icon, label, badge }) => {
            const isActive = location.pathname === to || (to !== "/" && location.pathname.startsWith(to));
            const badgeCount = badge === "sms_unread" ? smsUnread : 0;
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive
                    ? "bg-[#00A3E0] text-white font-medium shadow-md shadow-[#00A3E0]/30"
                    : "text-blue-200 hover:bg-[#1a5a91] hover:text-white"
                }`}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="truncate flex-1">{label}</span>
                {badgeCount > 0 && (
                  <span
                    className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold leading-none"
                    data-testid={`nav-badge-${label.toLowerCase()}`}
                  >
                    {badgeCount > 99 ? "99+" : badgeCount}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* User Footer */}
      <div className="p-3 border-t border-[#1a5a91]">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 bg-[#00A3E0] rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
            {user?.name?.charAt(0) || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.name}</p>
            <p className="text-blue-300 text-xs truncate">{user?.email}</p>
          </div>
          <button onClick={logout} className="text-blue-300 hover:text-white p-1 flex-shrink-0" title="Logout" data-testid="logout-btn">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-56 bg-[#0F4C81] flex-shrink-0">
        <NavContent />
      </aside>

      {/* Mobile Header + Hamburger
          `padding-top: env(safe-area-inset-top)` keeps the logo out from
          under the iOS status bar / dynamic island. Same trick on
          padding-left/right for landscape notch. */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#0F4C81] flex items-center justify-between px-4 py-3 shadow-lg"
        style={{
          paddingTop: "calc(env(safe-area-inset-top) + 0.75rem)",
          paddingLeft: "calc(env(safe-area-inset-left) + 1rem)",
          paddingRight: "calc(env(safe-area-inset-right) + 1rem)",
        }}
      >
        <Link to="/" className="flex items-center gap-2">
          <img src="https://customer-assets.emergentagent.com/job_e8b39f01-fa94-42d1-b033-5f5e8cd2cdfb/artifacts/i4ybnie3_C5B4C693-2106-4537-8F7F-AB72892ADD8D.png" alt="Tides Team" className="w-8 h-8 object-contain rounded" />
          <span className="text-white font-bold text-lg">Tides Hub</span>
        </Link>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white p-2 rounded-lg hover:bg-[#1a5a91]"
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-[#0F4C81] flex flex-col shadow-2xl">
            {/* Close button */}
            <div className="absolute top-3 right-3">
              <button onClick={() => setSidebarOpen(false)} className="text-white p-1 rounded hover:bg-[#1a5a91]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <NavContent />
          </aside>
        </div>
      )}

      {/* Main Content
          Extra bottom padding on mobile so page content can scroll past the
          floating "+" quick-create FAB (fixed at bottom-20 right-6) plus
          the iOS home indicator. Desktop keeps the tight layout since the
          FAB sits over the sidebar's negative space. */}
      <main
        className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden main-safe-area lg:pt-0"
      >
        {children}
      </main>

      {/* Global Quick Create FAB — hidden on the Inbox where the SMS
          composer already occupies the bottom-right thumb zone. */}
      {location.pathname !== "/inbox" && <QuickCreateFAB />}
    </div>
  );
}

function QuickCreateFAB() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const items = [
    { label: "New Contact", icon: Users, path: "/contacts?create=true", color: "bg-blue-500" },
    { label: "New Project", icon: FolderKanban, path: "/projects?create=true", color: "bg-emerald-500" },
    { label: "New Event", icon: Calendar, path: "/schedule?create=true", color: "bg-purple-500" },
    { label: "New Task", icon: CheckSquare, path: "/tasks?create=true", color: "bg-amber-500" },
  ];

  return (
    <div ref={ref} className="fixed bottom-20 right-6 z-50" data-testid="quick-create-fab">
      {open && (
        <div className="absolute bottom-16 right-0 bg-white rounded-xl shadow-2xl border border-slate-200 p-2 w-48 animate-in slide-in-from-bottom-2">
          {items.map(item => (
            <button
              key={item.label}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              onClick={() => { setOpen(false); navigate(item.path); }}
              data-testid={`quick-create-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className={`w-7 h-7 ${item.color} rounded-lg flex items-center justify-center`}>
                <item.icon className="w-4 h-4 text-white" />
              </div>
              {item.label}
            </button>
          ))}
        </div>
      )}
      <button
        className={`w-14 h-14 rounded-full bg-[#0F4C81] text-white shadow-lg hover:bg-[#0D4270] transition-all flex items-center justify-center ${open ? "rotate-45" : ""}`}
        onClick={() => setOpen(!open)}
        data-testid="quick-create-btn"
      >
        <Plus className="w-6 h-6" />
      </button>
    </div>
  );
}
